import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";

/**
 * Lightweight context menu rendered at fixed `x, y` mouse coordinates via
 * portal. Closes on Escape, click-anywhere, or selection.
 */
export default function ContextMenu({ x, y, open, onClose, items }) {
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose?.();
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDown);
    };
  }, [open, onClose]);

  if (!open) return null;
  return createPortal(
    <div
      ref={ref}
      style={{ position: "fixed", top: y, left: x, zIndex: 9999 }}
      className="bg-bg-elevated border border-line-strong shadow-[0_8px_24px_rgba(0,0,0,0.7)] min-w-[180px]"
      onClick={(e) => e.stopPropagation()}
      data-testid="row-context-menu"
    >
      {items.map((it, i) => (
        it.divider ? (
          <div key={`d-${i}`} className="border-t border-line my-px" />
        ) : (
          <button
            key={it.key || i}
            onClick={() => { onClose?.(); it.onClick?.(); }}
            disabled={it.disabled}
            className={`w-full text-left px-3 py-1.5 mono text-2xs uppercase tracking-widest transition-colors disabled:opacity-40 ${it.tone === "danger" ? "text-red hover:bg-red-glow" : "text-ink hover:bg-bg-highlight"}`}
            data-testid={`ctx-${it.key}`}
          >
            {it.label}
          </button>
        )
      ))}
    </div>,
    document.body,
  );
}
